import { useEffect, useState } from "react";
import { Trash2, Plus, Save, Eye, EyeOff, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inp = "w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-[#C9A45C]";
const btnGold = "bg-[#C9A45C] text-black px-4 py-2 text-sm font-semibold hover:bg-[#E4C987] transition-colors inline-flex items-center gap-2";
const full = (url) => (url && url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

async function uploadFile(file) {
  const fd = new FormData(); fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

function ImageField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="errah-label block mb-1 !text-[10px]">{label}</label>
      <div className="flex items-center gap-2">
        {value ? <img src={full(value)} alt="" className="w-14 h-14 object-cover hair-border" /> : <div className="w-14 h-14 hair-border flex items-center justify-center text-gray-600"><Upload size={14} /></div>}
        <label className="cursor-pointer text-xs text-[#C9A45C] hover:underline" data-testid={testid}>
          Upload<input type="file" accept="image/*" className="hidden" onChange={async (e) => { if (e.target.files[0]) { try { onChange(await uploadFile(e.target.files[0])); } catch { toast.error("Upload failed"); } } }} />
        </label>
      </div>
    </div>
  );
}

export function ProjectsManager() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/projects").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const blank = { title: "", location: "", category: "Homes", surface: "", solution: "SPF", description: "", image: "", before_image: "", after_image: "", published: true };
  const save = async () => {
    try { edit.id ? await api.put(`/admin/projects/${edit.id}`, edit) : await api.post("/admin/projects", edit); toast.success("Saved"); setEdit(null); load(); }
    catch { toast.error("Save failed"); }
  };
  return (
    <div data-testid="cms-projects">
      <div className="flex justify-between items-center mb-4"><h3 className="serif-head text-2xl">Projects</h3><button onClick={() => setEdit(blank)} className={btnGold} data-testid="add-project"><Plus size={15} /> Add project</button></div>
      {edit && (
        <div className="hair-border bg-warmblack p-5 mb-6 grid md:grid-cols-2 gap-3" data-testid="project-form">
          <input className={inp} placeholder="Title" value={edit.title} data-testid="proj-title" onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
          <input className={inp} placeholder="Location" value={edit.location} onChange={(e) => setEdit({ ...edit, location: e.target.value })} />
          <select className={inp} value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })}>{["Homes", "Kitchens", "Dining Tables", "Sofas", "Curtains", "Furniture", "Hotels", "Restaurants", "Offices"].map((c) => <option key={c}>{c}</option>)}</select>
          <input className={inp} placeholder="Surface" value={edit.surface} onChange={(e) => setEdit({ ...edit, surface: e.target.value })} />
          <input className={inp} placeholder="Solution (SPF / Nano)" value={edit.solution} onChange={(e) => setEdit({ ...edit, solution: e.target.value })} />
          <input className={inp} placeholder="Main image URL (or upload below)" value={edit.image} onChange={(e) => setEdit({ ...edit, image: e.target.value })} />
          <textarea className={`${inp} md:col-span-2`} placeholder="Description" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
          <ImageField label="Main image" value={edit.image} onChange={(v) => setEdit({ ...edit, image: v })} testid="proj-img" />
          <ImageField label="Before image" value={edit.before_image} onChange={(v) => setEdit({ ...edit, before_image: v })} testid="proj-before" />
          <ImageField label="After image" value={edit.after_image} onChange={(v) => setEdit({ ...edit, after_image: v })} testid="proj-after" />
          <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} /> Published</label>
          <div className="md:col-span-2 flex gap-2"><button onClick={save} className={btnGold} data-testid="save-project"><Save size={15} /> Save</button><button onClick={() => setEdit(null)} className="border border-white/20 px-4 py-2 text-sm">Cancel</button></div>
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((p) => (
          <div key={p.id} className="hair-border bg-warmblack p-4" data-testid={`cms-project-${p.id}`}>
            {p.image && <img src={full(p.image)} alt="" className="w-full h-28 object-cover mb-3" />}
            <div className="flex justify-between items-start"><div><p className="font-serif text-lg">{p.title}</p><p className="text-xs text-gray-500">{p.location} · {p.category}</p></div>
              <span className={`text-[10px] px-2 py-0.5 border ${p.published ? "text-[#C9A45C] border-[#C9A45C]/40" : "text-gray-500 border-white/15"}`}>{p.published ? "Live" : "Draft"}</span></div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setEdit(p)} className="text-xs text-gray-300 hover:text-white">Edit</button>
              <button onClick={async () => { await api.put(`/admin/projects/${p.id}`, { ...p, published: !p.published }); load(); }} className="text-xs text-gray-300 hover:text-white inline-flex items-center gap-1">{p.published ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}</button>
              <button onClick={async () => { await api.delete(`/admin/projects/${p.id}`); load(); }} className="text-xs text-red-400 hover:text-red-300 ml-auto"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DealersManager() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/dealers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const blank = { name: "", city: "", area: "", pincodes: "", services: [], phone: "", whatsapp: "", maps_url: "", status: "Active" };
  const save = async () => {
    const payload = { ...edit, services: typeof edit.services === "string" ? edit.services.split(",").map((s) => s.trim()).filter(Boolean) : edit.services };
    try { edit.id ? await api.put(`/admin/dealers/${edit.id}`, payload) : await api.post("/admin/dealers", payload); toast.success("Saved"); setEdit(null); load(); }
    catch { toast.error("Save failed"); }
  };
  return (
    <div data-testid="cms-dealers">
      <div className="flex justify-between items-center mb-4"><h3 className="serif-head text-2xl">Partners / Dealers</h3><button onClick={() => setEdit(blank)} className={btnGold} data-testid="add-dealer"><Plus size={15} /> Add partner</button></div>
      {edit && (
        <div className="hair-border bg-warmblack p-5 mb-6 grid md:grid-cols-2 gap-3" data-testid="dealer-form">
          <input className={inp} placeholder="Name" value={edit.name} data-testid="dealer-name" onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
          <input className={inp} placeholder="City" value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} />
          <input className={inp} placeholder="Area" value={edit.area} onChange={(e) => setEdit({ ...edit, area: e.target.value })} />
          <input className={inp} placeholder="PIN codes (comma separated)" value={edit.pincodes} onChange={(e) => setEdit({ ...edit, pincodes: e.target.value })} />
          <input className={inp} placeholder="Services (comma separated)" value={Array.isArray(edit.services) ? edit.services.join(", ") : edit.services} onChange={(e) => setEdit({ ...edit, services: e.target.value })} />
          <input className={inp} placeholder="Phone" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} />
          <input className={inp} placeholder="WhatsApp (digits only)" value={edit.whatsapp} onChange={(e) => setEdit({ ...edit, whatsapp: e.target.value })} />
          <input className={inp} placeholder="Google Maps URL" value={edit.maps_url} onChange={(e) => setEdit({ ...edit, maps_url: e.target.value })} />
          <select className={inp} value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>{["Active", "Pending", "Suspended"].map((s) => <option key={s}>{s}</option>)}</select>
          <div className="md:col-span-2 flex gap-2"><button onClick={save} className={btnGold} data-testid="save-dealer"><Save size={15} /> Save</button><button onClick={() => setEdit(null)} className="border border-white/20 px-4 py-2 text-sm">Cancel</button></div>
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((d) => (
          <div key={d.id} className="hair-border bg-warmblack p-4" data-testid={`cms-dealer-${d.id}`}>
            <div className="flex justify-between items-start"><div><p className="font-serif text-lg">{d.name}</p><p className="text-xs text-gray-500">{d.area}, {d.city} · {d.pincodes}</p></div>
              <span className={`text-[10px] px-2 py-0.5 border ${d.status === "Active" ? "text-[#C9A45C] border-[#C9A45C]/40" : "text-gray-500 border-white/15"}`}>{d.status}</span></div>
            <p className="text-xs text-gray-500 mt-2">{(d.services || []).join(", ")}</p>
            <div className="flex gap-2 mt-3"><button onClick={() => setEdit(d)} className="text-xs text-gray-300 hover:text-white">Edit</button>
              <button onClick={async () => { await api.delete(`/admin/dealers/${d.id}`); load(); }} className="text-xs text-red-400 ml-auto"><Trash2 size={13} /></button></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ContentManager() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/content/homepage").then(({ data }) => setC(data || {})); }, []);
  if (!c) return null;
  const save = async () => { try { await api.put("/admin/content/homepage", c); toast.success("Homepage content saved"); } catch { toast.error("Save failed"); } };
  const field = (k, label) => (
    <div><label className="errah-label block mb-1 !text-[10px]">{label}</label><input className={inp} value={c[k] || ""} data-testid={`content-${k}`} onChange={(e) => setC({ ...c, [k]: e.target.value })} /></div>
  );
  return (
    <div data-testid="cms-content" className="max-w-2xl">
      <h3 className="serif-head text-2xl mb-4">Homepage content</h3>
      <div className="hair-border bg-warmblack p-5 space-y-3">
        {field("hero_eyebrow", "Hero eyebrow")}
        {field("hero_title", "Hero title")}
        <div><label className="errah-label block mb-1 !text-[10px]">Hero description</label><textarea className={inp} value={c.hero_description || ""} onChange={(e) => setC({ ...c, hero_description: e.target.value })} /></div>
        {field("cta_primary", "Primary CTA")}
        {field("cta_secondary", "Secondary CTA")}
        <ImageField label="Hero image (optional override)" value={c.hero_image} onChange={(v) => setC({ ...c, hero_image: v })} testid="content-hero-img" />
        <button onClick={save} className={btnGold} data-testid="save-content"><Save size={15} /> Save</button>
      </div>
    </div>
  );
}

export function FaqManager() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/content/faqs").then(({ data }) => setItems(data?.items || [])); }, []);
  if (!items) return null;
  const save = async () => { try { await api.put("/admin/content/faqs", { items }); toast.success("FAQs saved"); } catch { toast.error("Save failed"); } };
  return (
    <div data-testid="cms-faqs" className="max-w-3xl">
      <div className="flex justify-between items-center mb-4"><h3 className="serif-head text-2xl">FAQs</h3><button onClick={() => setItems([...items, { q: "", a: "" }])} className={btnGold} data-testid="add-faq"><Plus size={15} /> Add FAQ</button></div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="hair-border bg-warmblack p-4 space-y-2" data-testid={`faq-edit-${i}`}>
            <input className={inp} placeholder="Question" value={it.q} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, q: e.target.value } : x))} />
            <textarea className={inp} placeholder="Answer" value={it.a} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, a: e.target.value } : x))} />
            <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-xs text-red-400 inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
          </div>
        ))}
      </div>
      <button onClick={save} className={`${btnGold} mt-4`} data-testid="save-faqs"><Save size={15} /> Save FAQs</button>
    </div>
  );
}
